import { IAPIRoutes } from "../../../../config";

// ─── API types ────────────────────────────────────────────────────────────────

export interface IColumnPreferencePresetApi {
  preset_id: string;
  name: string;
  selected_columns: string[];
  created_at: string;
  updated_at: string;
}

export interface IColumnPreferencesApiResponse {
  view_key: string;
  selected_columns: string[];
  updated_at: string;
  presets?: IColumnPreferencePresetApi[];
  /**
   * Server-tracked id of the preset whose columns currently match the global
   * `selected_columns`. Cleared by the backend when the user manually edits
   * the global selection. Used by the UI to disambiguate presets that share
   * identical column lists.
   */
  applied_preset_id?: string | null;
}

/**
 * Response shape for POST/PUT preset endpoints: the server returns the newly
 * created/updated preset plus the refreshed preset library (and view_key) so
 * the client can sync both in one round-trip.
 */
export interface IColumnPreferencePresetMutationResponse {
  view_key: string;
  preset: IColumnPreferencePresetApi;
  presets: IColumnPreferencePresetApi[];
}

/**
 * PUT /user-preferences/column-preferences accepts either a direct list of
 * columns (manual edit) OR a preset_id (apply preset → global selection).
 */
interface IColumnPreferencesGlobalPutRequest {
  selected_columns?: string[];
  preset_id?: string;
}

interface IColumnPreferencePresetCreateRequest {
  view_key: string;
  name: string;
  selected_columns: string[];
}

interface IColumnPreferencePresetPatchRequest {
  name?: string;
  selected_columns?: string[];
}

/**
 * Dedicated "apply preset" response: also returns the refreshed global
 * selection plus the applied preset for convenience.
 */
export interface IColumnPreferencePresetApplyResponse
  extends IColumnPreferencesApiResponse {
  applied_preset_id?: string;
}

// ─── Repository type ─────────────────────────────────────────────────────────

export type IColumnPreferencesRepository = ReturnType<
  typeof ColumnPreferencesRepositoryFactory
>;

// ─── Factory ─────────────────────────────────────────────────────────────────

export const ColumnPreferencesRepositoryFactory = ({
  APIProvider,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  APIRoutes: IAPIRoutes;
}) => {
  const { GET, POST, PUT, DELETE } = APIProvider;

  const getColumnPreferences = async (
    viewKey: string
  ): Promise<IColumnPreferencesApiResponse> => {
    // view_key is appended as a query string; Authorization is injected by
    // APIProvider.getDefaultHeaders() for /patient-centric-app-v2/ routes.
    return await GET(APIRoutes.apiGetColumnPreferences, { view_key: viewKey });
  };

  /**
   * Update the global selection. Accepts either an explicit column list
   * (manual toggle) or a preset_id (server copies that preset's columns
   * into the global selection).
   */
  const putColumnPreferences = async (
    viewKey: string,
    payload: IColumnPreferencesGlobalPutRequest
  ): Promise<IColumnPreferencesApiResponse> => {
    const url = `${
      APIRoutes.apiPutColumnPreferences
    }?view_key=${encodeURIComponent(viewKey)}`;
    return await PUT(url, payload);
  };

  const createColumnPreferencePreset = async (
    payload: IColumnPreferencePresetCreateRequest
  ): Promise<IColumnPreferencePresetMutationResponse> => {
    return await POST(APIRoutes.apiPostColumnPreferencePreset, payload);
  };

  const updateColumnPreferencePreset = async (
    viewKey: string,
    presetId: string,
    payload: IColumnPreferencePresetPatchRequest
  ): Promise<IColumnPreferencePresetMutationResponse> => {
    const base = APIRoutes.apiPutColumnPreferencePreset.replace(
      "{preset_id}",
      encodeURIComponent(presetId)
    );
    const url = `${base}?view_key=${encodeURIComponent(viewKey)}`;
    return await PUT(url, payload);
  };

  const deleteColumnPreferencePreset = async (
    viewKey: string,
    presetId: string
  ): Promise<unknown> => {
    const base = APIRoutes.apiDeleteColumnPreferencePreset.replace(
      "{preset_id}",
      encodeURIComponent(presetId)
    );
    const url = `${base}?view_key=${encodeURIComponent(viewKey)}`;
    return await DELETE(url);
  };

  const applyColumnPreferencePreset = async (
    viewKey: string,
    presetId: string
  ): Promise<IColumnPreferencePresetApplyResponse> => {
    const base = APIRoutes.apiApplyColumnPreferencePreset.replace(
      "{preset_id}",
      encodeURIComponent(presetId)
    );
    const url = `${base}?view_key=${encodeURIComponent(viewKey)}`;
    return await POST(url, {});
  };

  return {
    getColumnPreferences,
    putColumnPreferences,
    createColumnPreferencePreset,
    updateColumnPreferencePreset,
    deleteColumnPreferencePreset,
    applyColumnPreferencePreset,
  };
};
